import React, { useState, useEffect, useCallback, memo } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Menu,
  X,
  LayoutDashboard,
  BookText,
  Video,
  FileCheck,
  CalendarDays,
  GraduationCap,
  FileArchive,
  Users,
  LogOut,
  ChevronRight,
  FileText,
  Folder
} from "lucide-react";
import { GiTeacher } from "react-icons/gi";
import { FaWhatsapp, FaBell } from "react-icons/fa";
import PropTypes from "prop-types";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { query, ref, onValue, orderByChild, equalTo, get, update } from "firebase/database";
import logo from "./assets/Xmaths.png";
import { formatCourseId } from "./utils/formatCourse";
import CircularText from "./CircularText";

const Layout = memo(() => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filesMenuOpen, setFilesMenuOpen] = useState(false);
  const [username, setUsername] = useState("Loading...");
  const [studentId, setStudentId] = useState("Loading...");
  const [initials, setInitials] = useState("");
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname.split('/').pop() || 'Dashboard';

  const fetchUserData = useCallback(async (user) => {
    if (!user) {
      setUsername("Not Logged In");
      setStudentId("No ID");
      return;
    }

    try {
      const email = user.email;
      let foundUser = null;
      let courseName = null;

      // First check if user is admin/assistant in users collection
      const usersRef = ref(db, "users");
      const usersSnapshot = await get(usersRef);

      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        foundUser = Object.values(usersData).find(
          (u) => u.email === email
        );
      }

      // If not found in users collection, search in courses/students
      if (!foundUser) {
        const coursesRef = ref(db, "courses");
        const coursesSnapshot = await get(coursesRef);

        if (coursesSnapshot.exists()) {
          const courses = coursesSnapshot.val();

          // Search through each course's students
          for (const [courseId, courseData] of Object.entries(courses)) {
            if (courseData.students) {
              const student = Object.values(courseData.students).find(
                (s) => s.email === email
              );
              if (student) {
                foundUser = student;
                courseName = courseId;
                break;
              }
            }
          }
        }
      }

      if (foundUser) {
        const fullName = foundUser.name;
        const nameParts = fullName.split(" ");
        const firstName = nameParts[0];
        const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

        setUsername(firstName);
        setStudentId(foundUser.studentId || "Unknown ID");
        setInitials(`${firstName.charAt(0)}${lastName ? lastName.charAt(0) : ""}`.toUpperCase());
        setEnrolledCourse(courseName);
      } else {
        setUsername("Unknown User");
        setStudentId("Unknown ID");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUsername("Error Loading User");
      setStudentId("Error Loading ID");
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, fetchUserData);
    return () => unsubscribe();
  }, [fetchUserData]);

  const handleLogout = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Check if user is a student
        const coursesRef = ref(db, "courses");
        const coursesSnapshot = await get(coursesRef);

        if (coursesSnapshot.exists()) {
          const courses = coursesSnapshot.val();
          let updatePromises = [];

          // Search through all courses for the student
          for (const [courseId, courseData] of Object.entries(courses)) {
            if (courseData.students) {
              const student = Object.values(courseData.students).find(
                (s) => s.email === currentUser.email
              );
              if (student) {
                // Update isLoggedIn status to false
                const studentRef = ref(db, `courses/${courseId}/students/${student.studentId}`);
                updatePromises.push(update(studentRef, { isLoggedIn: false }));
              }
            }
          }

          // Wait for all updates to complete
          if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
          }
        }
      }

      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  }, [navigate]);

  const openWhatsAppChat = useCallback(() => {
    const phoneNumber = "201004672283";
    const message = "Hello!";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  }, []);

  const navItems = [
    { name: "Dashboard", path: "/dashboard", icon: <LayoutDashboard size={20} /> },
    { name: "Progress", path: "/dashboard/chapters", icon: <BookText size={20} /> },
    { name: "Videos", path: "/dashboard/videos", icon: <Video size={20} /> },
    { name: "PDFs", path: "/dashboard/pdfs", icon: <FileText size={20} /> },
    // {
    //   name: "Files",
    //   icon: <Folder size={20} />,
    //   subItems: [
    //     { name: "Mark Schemes", path: "/dashboard/markschemes", icon: <FileCheck size={20} /> },
    //     { name: "Extra Questions", path: "/dashboard/extra-questions", icon: <FileText size={20} /> },
    //     { name: "Past Papers", path: "/dashboard/pastpapers", icon: <FileArchive size={20} /> },
    //   ]
    // },
    { name: "Homework", path: "/dashboard/homeworks", icon: <CalendarDays size={20} /> },
    { name: "Exams Grades", path: "/dashboard/exams", icon: <GraduationCap size={20} /> },
    // { name: "Online Meetings", path: "/dashboard/online-meetings", icon: <Users size={20} /> },
    { name: "Announcements", path: "/dashboard/announcements", icon: <FaBell size={20} /> },
  ];

  return (
    <div className="flex bg-gray-950 text-white min-h-screen">
      {/* Sidebar Overlay for mobile */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setSidebarOpen(false)}></div>

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 h-full w-72 bg-gray-800 shadow-xl transition-transform duration-300 z-50 ${sidebarOpen ? 'translate-x-0' : '-translate-x-72'} sm:translate-x-0 flex flex-col`}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 shadow-lg">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center">
              <div class="ml-16">
                <div class="scale-50 -m-16">
                  <div className="bg-[url('/Xmaths.png')] bg-center bg-[length:120px] bg-no-repeat filter brightness-0 invert">
                    <CircularText
                      text="✦ SHEMIES ✦ MATH ✦ IGCSE "
                      onHover="speedUp"
                      spinDuration={20}
                      className=""
                    />
                  </div>
                </div>
              </div>
            </Link>
            <button className="sm:hidden text-white" onClick={() => setSidebarOpen(false)}>
              <X size={24} />
            </button>
          </div>
          {enrolledCourse && (
            <div className="mt-4 text-sm text-white/90">Course: {formatCourseId(enrolledCourse)}</div>
          )}
        </div>

        {/* Sidebar Navigation */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {/* Navigation with Hover Effects */}
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.name}>
                {item.subItems ? (
                  <div>
                    <button
                      onClick={() => setFilesMenuOpen(!filesMenuOpen)}
                      className={`flex items-center w-full p-2 rounded-xl transition-all duration-300 group ${filesMenuOpen ? "bg-gradient-to-r from-teal-900/40 to-sky-900/40 text-white shadow-inner" : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                        }`}
                    >
                      <span
                        className={`p-1 rounded-lg mr-3 transition-colors ${filesMenuOpen ? "bg-teal-600/20 text-teal-400" : "bg-gray-800/50 text-gray-500 group-hover:bg-teal-600/20 group-hover:text-teal-400"
                          }`}
                      >
                        {item.icon}
                      </span>
                      <span className="font-medium">{item.name}</span>
                      <ChevronRight
                        size={16}
                        className={`ml-auto transition-transform duration-300 ${filesMenuOpen ? "rotate-90" : ""
                          }`}
                      />
                    </button>
                    {filesMenuOpen && (
                      <ul className="ml-6 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <li key={subItem.name}>
                            <Link
                              to={subItem.path}
                              className={`flex items-center w-full p-2 rounded-xl transition-all duration-300 group ${location.pathname.startsWith(subItem.path)
                                ? "bg-gradient-to-r from-teal-900/40 to-sky-900/40 text-white shadow-inner"
                                : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                                }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <span
                                className={`p-1 rounded-lg mr-3 transition-colors ${location.pathname.startsWith(subItem.path)
                                  ? "bg-teal-600/20 text-teal-400"
                                  : "bg-gray-800/50 text-gray-500 group-hover:bg-teal-600/20 group-hover:text-teal-400"
                                  }`}
                              >
                                {subItem.icon}
                              </span>
                              <span className="font-medium">{subItem.name}</span>
                              <span
                                className={`ml-auto w-2 h-2 rounded-full transition-all ${location.pathname.startsWith(subItem.path)
                                  ? "bg-teal-400 scale-150"
                                  : "bg-gray-700 group-hover:bg-teal-400/30"
                                  }`}
                              ></span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center w-full p-2 rounded-xl transition-all duration-300 group ${item.path === "/dashboard"
                      ? location.pathname === "/dashboard"
                      : location.pathname.startsWith(item.path)
                        ? "bg-gradient-to-r from-teal-900/40 to-sky-900/40 text-white shadow-inner"
                        : "text-gray-400 hover:bg-gray-800/50 hover:text-white"
                      }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span
                      className={`p-1 rounded-lg mr-3 transition-colors ${item.path === "/dashboard"
                        ? location.pathname === "/dashboard"
                        : location.pathname.startsWith(item.path)
                          ? "bg-teal-600/20 text-teal-400"
                          : "bg-gray-800/50 text-gray-500 group-hover:bg-teal-600/20 group-hover:text-teal-400"
                        }`}
                    >
                      {item.icon}
                    </span>
                    <span className="font-medium">{item.name}</span>
                    <span
                      className={`ml-auto w-2 h-2 rounded-full transition-all ${item.path === "/dashboard"
                        ? location.pathname === "/dashboard"
                        : location.pathname.startsWith(item.path)
                          ? "bg-teal-400 scale-150"
                          : "bg-gray-700 group-hover:bg-teal-400/30"
                        }`}
                    ></span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Logout Button with Animation */}
        <div className="pt-6 px-4 pb-0">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-full p-4 bg-gradient-to-r from-red-900/40 to-pink-900/40 rounded-xl hover:from-red-800/50 hover:to-pink-800/50 transition-all duration-300 text-white border border-red-900/30 hover:border-red-700/50 shadow-lg hover:shadow-red-500/10 group mb-0"
          >
            <LogOut size={20} className="mr-3 group-hover:rotate-180 transition-transform duration-500" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>

        {/* Sidebar Footer */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-600 font-mono">Powered by Shemies</p>
        </div>
      </div>

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>

      {/* Main Content Area */}
      <div className="flex flex-col w-full sm:ml-72">
        {/* Floating Top Bar */}
        <div className="fixed top-0 left-0 w-full bg-gray-900/80 backdrop-blur-lg p-3 flex justify-between items-center z-40 sm:pl-72 border-b border-gray-800/50">
          <div className="flex items-center">
            <button
              className="sm:hidden text-gray-400 hover:text-white transition-transform hover:scale-110 duration-200"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>
            {/* Mobile Logo */}
            <div className="sm:hidden ml-4">
              <div class="-mt-16 mx-4">
                <div class="scale-[0.35] -m-16">
                  <div className="bg-[url('/Xmaths.png')] bg-center bg-[length:120px] bg-no-repeat filter brightness-0 invert">
                    <CircularText
                      text="✦ SHEMIES ✦ MATH ✦ IGCSE "
                      onHover="speedUp"
                      spinDuration={20}
                      className=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Card */}
          <div
            className="flex items-center space-x-3 bg-gray-800/30 px-3 py-1.5 rounded-lg border border-gray-700/50 backdrop-blur-sm hover:border-teal-500/30 transition-all duration-300 cursor-pointer"
            onClick={() => navigate('/dashboard/profile')}
          >
            <div className="relative">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-sky-700 text-white capitalize rounded-lg flex items-center justify-center text-sm font-bold shadow-lg">
                {initials}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-800"></div>
            </div>
            <div>
              <p className="font-medium text-white text-sm leading-tight">{username}</p>
              <p className="text-xs text-teal-400/80 font-mono leading-tight">{studentId}</p>
            </div>
          </div>
        </div>

        {/* Page Content with Glass Panel */}
        <div className="min-h-screen pt-20 sm:pt-20 px-3 sm:px-4 md:px-6">
          <div className="shadow-2xl">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
});

Layout.propTypes = {
  children: PropTypes.node
};

export default Layout;